import { createElement } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import Widget from './Widget';
import type {
  WidgetEntityType,
  IdentifierType,
  DisplayMode,
  WidgetConfig,
  WidgetProps,
  WidgetTheme,
} from './types';

/**
 * PlumX Widget Web Component.
 *
 * Embed research metrics on any third-party website:
 *
 * @example
 * <script src="https://plumx.elsevier.com/widget/plumx-widget.js"></script>
 * <plumx-widget
 *   type="artifact"
 *   identifier-type="doi"
 *   identifier="10.1016/j.jare.2021.08.003"
 *   display="plumprint"
 *   site="plumx"
 * ></plumx-widget>
 */
class PlumxWidget extends HTMLElement {
  private root: Root | null = null;
  private abortController: AbortController | null = null;
  private mounted = false;
  private lastAttributeSignature: string = ''; // Track attributes to avoid unnecessary remounts

  static get observedAttributes(): string[] {
    return ['type', 'identifier-type', 'identifier', 'display', 'config', 'site', 'theme', 'lang'];
  }

  connectedCallback(): void {
    this.mounted = true;
    this.abortController = new AbortController();
    this.mount();
  }

  attributeChangedCallback(): void {
    if (this.mounted) {
      // Only remount if attributes actually changed
      const currentSignature = this.getAttributeSignature();
      if (currentSignature !== this.lastAttributeSignature) {
        this.lastAttributeSignature = currentSignature;
        this.mount();
      }
    }
  }

  disconnectedCallback(): void {
    this.mounted = false;
    this.abortController?.abort();
    this.root?.unmount();
    this.root = null;
  }

  private getAttributeSignature(): string {
    const observed = PlumxWidget.observedAttributes;
    return observed.map(attr => `${attr}=${this.getAttribute(attr)}`).join('|');
  }

  private parseConfig(): WidgetConfig {
    try {
      return JSON.parse(this.getAttribute('config') ?? '{}') as WidgetConfig;
    } catch {
      console.warn('[plumx-widget] Invalid JSON in config attribute');
      return {};
    }
  }

  private getProps(signal: AbortSignal): WidgetProps {
    return {
      type: (this.getAttribute('type') ?? 'artifact') as WidgetEntityType,
      identifierType: (this.getAttribute('identifier-type') ?? 'doi') as IdentifierType,
      identifier: this.getAttribute('identifier') ?? '',
      display: (() => {
        const raw = this.getAttribute('display') ?? 'plumprint';
        return (raw === 'popup' ? 'plumprint' : raw) as DisplayMode;
      })(),
      config: this.parseConfig(),
      site: this.getAttribute('site') ?? 'plumx',
      theme: (() => {
        const raw = this.getAttribute('theme');
        if (!raw) return undefined;

        const normalized = raw.toLowerCase().replace(/\s+/g, '').replace(/-/g, '');
        if (normalized === 'default') return 'default';
        if (normalized === 'bigben') return 'bigben';
        if (normalized === 'liberty') return 'liberty';

        return undefined;
      })() as WidgetTheme | undefined,
      lang: this.getAttribute('lang') ?? 'en',
      signal,
      onEvent: (event, detail) => {
        this.dispatchEvent(
          new CustomEvent(`plumx:${event}`, { bubbles: true, composed: true, detail }),
        );
      },
    };
  }

  private mount(): void {
    if (!this.mounted || !this.abortController) return;

    if (!this.root) {
      const container = document.createElement('div');
      this.appendChild(container);
      this.root = createRoot(container);
    }

    this.root.render(createElement(Widget, this.getProps(this.abortController.signal)));
  }
}

if (!customElements.get('plumx-widget')) {
  customElements.define('plumx-widget', PlumxWidget);
}
